import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { stripeConnectApi, stripeDashboardLinkApi, stripeStatusApi } from '@/lib/api/organizationsApi';
import { ApiError } from '@api-client/client';
import { StripeAccountStatus } from '@shared/enums/domain.enum';
import { AlertCircle, CheckCircle, CreditCard, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STRIPE_STATUS_CONFIG: Record<StripeAccountStatus, { label: string; badge: string; icon: React.ReactNode }> = {
  [StripeAccountStatus.PENDING]: {
    label: 'Non configuré',
    badge: 'bg-slate-100 text-slate-600',
    icon: <AlertCircle className='h-4 w-4' />,
  },
  [StripeAccountStatus.ONBOARDING]: {
    label: "En cours d'activation",
    badge: 'bg-amber-100 text-amber-700',
    icon: <RefreshCw className='h-4 w-4' />,
  },
  [StripeAccountStatus.ACTIVE]: {
    label: 'Actif',
    badge: 'bg-emerald-100 text-emerald-700',
    icon: <CheckCircle className='h-4 w-4' />,
  },
  [StripeAccountStatus.RESTRICTED]: {
    label: 'Restreint',
    badge: 'bg-orange-100 text-orange-700',
    icon: <AlertCircle className='h-4 w-4' />,
  },
  [StripeAccountStatus.DISABLED]: {
    label: 'Désactivé',
    badge: 'bg-red-100 text-red-700',
    icon: <AlertCircle className='h-4 w-4' />,
  },
};

interface StripeWidgetProps {
  orgId: number;
  stripeAccountStatus: StripeAccountStatus;
  stripeTestMode?: boolean;
  /** Gating D6 : SIREN requis avant de connecter Stripe — désactive le CTA de connexion */
  sirenMissing?: boolean;
}

export function StripeWidget({ orgId, stripeAccountStatus, stripeTestMode, sirenMissing }: StripeWidgetProps) {
  const [loading, setLoading] = useState<'connect' | 'dashboard' | null>(null);
  const cfg = STRIPE_STATUS_CONFIG[stripeAccountStatus];
  const isActive = stripeAccountStatus === StripeAccountStatus.ACTIVE;
  const statusDescription: Record<StripeAccountStatus, string> = {
    [StripeAccountStatus.PENDING]: 'Stripe n’est pas encore connecte. Les concours payants seront bloques.',
    [StripeAccountStatus.ONBOARDING]: 'L’activation Stripe est en cours. Terminez l’onboarding pour encaisser.',
    [StripeAccountStatus.ACTIVE]:
      'Votre compte Stripe est actif. Les paiements des concours payants peuvent etre encaisses.',
    [StripeAccountStatus.RESTRICTED]:
      'Stripe demande des informations complementaires avant d’autoriser les paiements.',
    [StripeAccountStatus.DISABLED]: 'Votre compte Stripe est desactive. Les concours payants sont bloques.',
  };

  function toastApiError(err: unknown, fallback: string) {
    if (err instanceof ApiError && err.toastShown) return;
    toast.error(err instanceof Error ? err.message : fallback);
  }

  async function handleConnect() {
    setLoading('connect');
    try {
      const { url } = await stripeConnectApi(orgId);
      window.location.href = url;
    } catch (err) {
      toastApiError(err, 'Erreur lors de la connexion Stripe.');
      setLoading(null);
    }
  }

  async function handleRefreshStatus() {
    setLoading('connect');
    try {
      await stripeStatusApi(orgId);
      window.location.reload();
    } catch (err) {
      toastApiError(err, 'Erreur lors de la vérification du statut Stripe.');
    } finally {
      setLoading(null);
    }
  }

  async function handleDashboard() {
    setLoading('dashboard');
    try {
      const { url: loginUrl } = await stripeDashboardLinkApi(orgId);
      window.open(loginUrl, '_blank');
    } catch (err) {
      // Statut front périmé possible (ex. compte désactivé côté Stripe depuis
      // le dernier chargement) — feedback utilisateur au lieu d'une promesse
      // rejetée silencieuse remontée à Sentry.
      toastApiError(err, "Erreur lors de l'ouverture du dashboard Stripe.");
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-6'>
      <h3 className='mb-4 flex items-center gap-2 font-bold text-slate-900'>
        <CreditCard className='h-5 w-5 text-slate-600' />
        Paiements Stripe Connect
      </h3>
      {stripeTestMode && (
        <div className='mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2'>
          <p className='text-xs font-semibold text-amber-700'>Mode test Stripe actif</p>
          <p className='mt-0.5 text-xs text-amber-600'>
            Aucun paiement r&eacute;el. Utilisez les{' '}
            <a
              href='https://docs.stripe.com/testing#cards'
              target='_blank'
              rel='noreferrer'
              className='underline hover:text-amber-800'
            >
              cartes de test Stripe
            </a>{' '}
            pour simuler des transactions.
          </p>
        </div>
      )}
      <div className='mb-3 flex items-center gap-2'>
        <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cfg.badge}`}>
          {cfg.icon}
          {cfg.label}
        </span>
      </div>

      <p className='mb-4 text-sm text-slate-600'>{statusDescription[stripeAccountStatus]}</p>

      {!isActive ? (
        <div className='space-y-3'>
          <div className='rounded-xl border border-amber-200 bg-amber-50 px-3 py-2'>
            <p className='text-xs font-semibold text-amber-800'>Action requise avant concours payant</p>
            <p className='mt-0.5 text-xs text-amber-700'>
              Les inscriptions gratuites restent possibles, mais les concours payants ne peuvent pas etre publies ou
              payes tant que Stripe n&apos;est pas actif.
            </p>
          </div>
          {sirenMissing && (
            <p className='text-xs font-semibold text-amber-700'>
              <Link to={`${ROUTES.ORGANIZER_PROFILE_EDIT}#organization-siret`} className='underline'>
                Renseignez le SIRET dans le profil de votre organisation
              </Link>{' '}
              pour connecter Stripe. Le SIREN sera déduit automatiquement.
            </p>
          )}
          <button
            onClick={handleConnect}
            disabled={loading !== null || sirenMissing}
            className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {loading === 'connect' ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <ExternalLink className='h-4 w-4' />
            )}
            {stripeAccountStatus === StripeAccountStatus.PENDING
              ? 'Connecter mon compte Stripe'
              : "Continuer l'activation"}
          </button>
          {stripeAccountStatus === StripeAccountStatus.ONBOARDING && (
            <button
              onClick={handleRefreshStatus}
              disabled={loading !== null}
              className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60'
            >
              <RefreshCw className='h-4 w-4' />
              V&eacute;rifier le statut
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={handleDashboard}
          disabled={loading !== null}
          className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-60'
        >
          {loading === 'dashboard' ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <ExternalLink className='h-4 w-4' />
          )}
          Tableau de bord Stripe
        </button>
      )}
    </div>
  );
}
